import {
    ECharts,
    EChartsOption,
    TooltipComponentPositionCallback,
} from 'echarts';

/** Represents a tooltip entry that can be either a section title (name only) or a metric row (name + key), with an optional value formatter. */
export interface BITooltipValueOption {
    /** The key in the data object (if set, the entry is a metric). */
    key?: string;
    /** Display the title section (if no `key` is specified) or the metric label (if `key` is specified). */
    name: string;
    /** An optional function to format the metric's value for display.
     * @default Uses `tooltipNumberValueFormatter` to format numbers to a fixed precision of 2 decimal places in the en-US locale.
     */
    formatter?: (value: any) => string;
}

/** Options for configuring the behavior and appearance of tooltips in ECharts visualizations. */
export interface BITooltipOptions {
    /** Function to format the label of the x-axis in the tooltip. */
    xLabelFormatter?: (label: any) => string;
    /** Function to format the main value displayed in the tooltip. */
    valueFormatter?: (value: any) => string;
    /** Additional metrics to be displayed in the tooltip. */
    secondaryMetrics?: BITooltipValueOption[];
    /** Split name truncate limit, default 200 */
    splitNameTruncateLimit?: number;
}

export class EChartsTooltipHelper {
    /**
     * Formats a value for display in a tooltip.
     *
     * This function is designed to format numbers to a fixed precision of 2 decimal places in the en-US locale.
     * If the value is a number, it formats it to have comma as a thousand separator and two decimal points (e.g., 1234567.8901234 -> 1,234,567.89).
     * Additionally, any provided suffix is appended to the formatted number.
     * If the value is an instance of a Date, a console log is generated, and the date is returned as is.
     * If the value is null, an empty string is returned.
     * For other types of values or if the value is falsy, the original value with the suffix is returned.
     *
     * @param value - The value to be formatted. It can be of any type.
     * @param suffix - An optional string that will be appended to the formatted number.
     * @param precision - An optional number specifying the number of decimal places to format the number to. If not provided, the precision is determined based on the number of significant digits in the decimal part of the number.
     * @returns The formatted value as per the specified rules.
     */
    static tooltipNumberValueFormatter(
        value: any,
        suffix: string = '',
        precision?: number
    ): any {
        // if value is misssing
        if (value === undefined) {
            return '';
        } else if (typeof value === 'number') {
            // cast to en-US locale with fixed precision of 2
            // example: 1234567.8901234 -> 1,234,567.89

            // adapt precision to amount of zeroes after decimal point
            if (precision === undefined) {
                const valueString = value.toString();
                const decimalIndex = valueString.indexOf('.');
                if (decimalIndex !== -1) {
                    const decimalPart = valueString.slice(decimalIndex + 1);
                    const firstNonZeroIndex = decimalPart.search(/[1-9]/);
                    if (firstNonZeroIndex !== -1) {
                        precision = Math.min(firstNonZeroIndex + 2, 20); // limit to max 20
                    } else {
                        precision = 2; // all zeroes
                    }
                } else {
                    precision = 0; // no decimal part
                }
            }
            return (
                value.toLocaleString('en-US', {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision,
                }) + suffix
            );
        } else if (value instanceof Date) {
            console.log(
                `echarts.tooltipNumberValueFormatter: ${value} is Date`
            );
            return value;
        }
        // else if null
        else if (value === null) {
            return '';
        }
        return value;
    }

    public static tooltipCurrencyValueFormatter(
        value: any,
        currency: string,
        precision: number = 2
    ): any {
        // console.log({ msg: 'tooltipCurrencyValueFormatter', value, currency });
        if (typeof value === 'number') {
            // cast to en-US locale with fixed precision of 2
            // example: 1234567.8901234 -> 1,234,567.89
            return value.toLocaleString('en-US', {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision,
                style: 'currency',
                currency: currency,
            });
        }
        // else if null or undefined return empty string
        else if (value === null || value === undefined) {
            return '';
        }

        // else throw NotImplementedError
        else {
            throw new Error(
                `tooltipCurrencyValueFormatter: ${value} is not a number`
            );
        }
    }

    public static tooltipIntegerValueFormatter(v: number): string {
        // if null return empty string
        if (v === null) {
            return '';
        } else {
            return v?.toLocaleString('en-US', {
                maximumFractionDigits: 0,
            });
        }
    }

    public static tooltipPercentValueFormatter(v: number): string {
        return v?.toLocaleString('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    /**
     * This function returns the default tooltip for the echarts dashboard item.
     */
    public static createDefaultTooltip(
        echartsInstance: ECharts,
        options: BITooltipOptions
        // {
        //     xLabelFormatter?: (label: any) => string;
        //     valueFormatter?: (value: any) => string;
        // }
    ): EChartsOption['tooltip'] {
        const echartsDom = echartsInstance.getDom();
        const tooltipOption: EChartsOption['tooltip'] = {
            trigger: 'axis',
            transitionDuration: 0,
            hideDelay: 0,
            appendToBody: true,
            // absolute position, which is 10px to the left side and 10px to the top side of the container
            position:
                EChartsTooltipHelper.createDefaultPositionCallback(echartsDom),
            formatter: EChartsTooltipHelper.createTooltipFormatter(
                options.xLabelFormatter,
                options.valueFormatter,
                options.secondaryMetrics,
                options.splitNameTruncateLimit
            ),
        };
        return tooltipOption;
    }

    /** Create a default position callback for tooltips in ECharts.
     *
     * The tooltip will be positioned based on the mouse cursor's position within the ECharts container.
     * It ensures that the tooltip remains fully visible within the viewport, adjusting its position
     * to avoid overflow beyond the screen edges.
     * @param echartsDom - The DOM element of the ECharts instance.
     * @returns A callback function that calculates the tooltip's position.
     */
    private static createDefaultPositionCallback(
        echartsDom: HTMLElement
    ): TooltipComponentPositionCallback {
        return (pos, params, dom, rect, size) => {
            let domRect = echartsDom.getBoundingClientRect();
            const obj: { [key: string]: number } = {};

            const offset = 10;
            // mouse absolute positions from domRect
            const mouseX = pos[0] + domRect.left;
            const mouseY = pos[1] + domRect.top;

            const tooltipHeight = size.contentSize[1];
            const screenHeight = window.innerHeight; // Actual screen height

            const tooltipWidth = size.contentSize[0];
            const screenWidth = window.innerWidth; // Actual screen width

            // spaceBelow domRect.bottom
            const spaceBelowMouse = screenHeight - mouseY;
            const spaceBelowDomRect = screenHeight - domRect.bottom;

            const spaceRightMouse = screenWidth - mouseX;
            const spaceRightDomRect = screenWidth - domRect.right;

            // Check if tooltip is taller than the screen height
            if (tooltipHeight + 2 * offset > screenHeight) {
                obj['top'] = -domRect.top + offset;
            }
            // If tooltip doesn't fit below the cursor, display it so that bottom of the tooltip is on the bottom of the screen
            else if (tooltipHeight + 2 * offset > spaceBelowMouse) {
                obj['bottom'] = -spaceBelowDomRect + offset;
            }
            // Default: Display tooltip below the cursor
            else {
                obj['top'] = pos[1] + offset;
            }

            // If tooltip doesn't fit on the right side of the cursor, display it on the left side
            if (tooltipWidth + 2 * offset > spaceRightMouse) {
                obj['right'] = size.viewSize[0] - pos[0] + 20;
            }
            // Default: Display tooltip on the right side of the cursor
            else {
                obj['left'] = pos[0] + offset;
            }

            return obj;
        };
    }

    /**
     * Creates a tooltip formatter function for visual components.
     *
     * @param xLabelFormatter - A callback that formats the xLabel. This can take multiple arguments,
     *        e.g., (value, currencySymbol, dateAggregation). By default, it returns the label as is.
     * @param valueFormatter - A callback that formats the tooltip value. This can take multiple arguments,
     *        e.g., (value, currencySymbol, dateAggregation). By default, it uses the `tooltipNumberValueFormatter` function.
     * @returns A function that takes `params` and `_ticket` as arguments and returns a formatted string for tooltips.
     *          The returned function checks if `params` is an array or an object and constructs an HTML string
     *          with styles and formatted data to be shown as a tooltip.
     *
     * Usage:
     * const tooltipFormatter = tooltipFormatterFactory(myLabelFormatter, myValueFormatter);
     * const tooltipString = tooltipFormatter(someParams, someTicket);
     */
    private static createTooltipFormatter(
        xLabelFormatter: (label: any) => string = (label) => label,
        valueFormatter: (
            value: any
        ) => string = EChartsTooltipHelper.tooltipNumberValueFormatter,
        secondaryMetrics?: BITooltipValueOption[],
        /** Split name truncate limit, default 200 */
        splitNameTruncateLimit?: number
    ): (params: any, _ticket: string) => string {
        const truncateLimit = splitNameTruncateLimit ?? 200;
        return function (params: any) {
            const formatXAxis = (label: any) =>
                `<div style="font-size:14px;color:#666;font-weight:400;line-height:1;">${xLabelFormatter(
                    label
                )}</div>`;

            const formatSeries = (p: any, idx: number) => {
                const mainValue = p.data[p.dimensionNames[p.encode.y[0]]];
                if (mainValue === null) {
                    return '';
                }

                // --- główna metryka (series) ---
                let html = `<div style="margin:${
                    idx === 0 ? '0px' : '10px'
                } 0 0;line-height:1;">
        <div style="display:grid;grid-template-columns:auto max-content;align-items:center;gap:8px;line-height:15px;">
            <div style="display:flex;align-items:center;gap:4px;">
                ${p.marker}
                <span style="font-size:14px;color:#666;font-weight:400;">${
                    // truncate longer than 120 chars
                    p.seriesName.length > truncateLimit
                        ? p.seriesName.substring(0, truncateLimit - 20) +
                          '... <i>[truncated due to size]</i>'
                        : p.seriesName
                }</span>
            </div>
            <div style="font-size:14px;color:#666;font-weight:900;">${valueFormatter(
                mainValue
            )}</div>
        </div>
    </div>`;

                // --- dodatkowe metryki (values) ---
                if (secondaryMetrics?.length) {
                    const titleRow = (title: string) =>
                        `<div style="display:grid;grid-template-columns:auto max-content;gap:10px;line-height:20px;margin-left:18px;font-size:12px;color:#666;margin-top:5px;">${title}</div>`;

                    const metricRow = (label: string, value: string) =>
                        `<div style="display:grid;grid-template-columns:auto max-content;gap:10px;line-height:20px;margin-left:18px;font-size:12px;color:#666;">
       <div>${label}</div>
       <div style="font-weight:600;">${value}</div>
     </div>`;

                    const secondary = secondaryMetrics
                        .map(({ name, key, formatter }) => {
                            if (!name) return '';
                            if (!key) {
                                return titleRow(name);
                            }
                            const raw = p?.data?.[key];
                            const formatted = formatter
                                ? formatter(raw)
                                : EChartsTooltipHelper.tooltipNumberValueFormatter(
                                      raw
                                  );

                            return metricRow(name, formatted);
                        })
                        .join('');

                    html += secondary;
                }

                return html;
            };

            const xAxis =
                params instanceof Array
                    ? formatXAxis(params[0].axisValue)
                    : formatXAxis(params.name);
            const series =
                params instanceof Array
                    ? params
                          .sort(
                              (a: any, b: any) =>
                                  b.data[b.dimensionNames[b.encode.y[0]]] -
                                  a.data[a.dimensionNames[a.encode.y[0]]]
                          )
                          .map(formatSeries)
                          .join('')
                    : formatSeries(params, 1);

            if (series === '') {
                return '';
            }

            const seriesContainer = `
            <div style="margin: 10px 0 0;line-height:1;">
                <div style="margin: 0px 0 0;line-height:1;">${series}</div>
            </div>`;

            return `<div style="min-width:100px">${xAxis}${seriesContainer}</div>`;
        };
    }
}
